/* Direcao visual PREMIUM ("Obsidian Aurora"), deliberadamente distinta das 128
   telas do prototipo: esta e pagina publica, nao tela de operacao. A paleta vive
   em packages/design-system/src/styles/premium.css e no AmbientBackground.
   Nao aplicar aqui o retint enterprise. */
'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../shared/auth/AuthContext';
import { AmbientBackground } from '../../shared/components/AmbientBackground';

/* ──────────────────────────────────────────────────────────
   Nexora — Premium Obsidian Aurora Landing
   Tokens: obsidian #05070a · aurora #06b6d4→#0ea5e9 · gold #f59e0b
   Glass, shimmer, glow, bento, 3D mock, carousel, tabs, pricing
   ────────────────────────────────────────────────────────── */

// ── Helpers ────────────────────────────────────────────────
function useScrollScrolled(threshold = 16) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const cb = () => setScrolled(window.scrollY > threshold);
    cb();
    window.addEventListener('scroll', cb, { passive: true });
    return () => window.removeEventListener('scroll', cb);
  }, [threshold]);
  return scrolled;
}

function useCounter(target: number, active: boolean, decimals = 0) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    const start = performance.now();
    const dur = 1800;
    const tick = (now: number) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      setVal(eased * target);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target]);
  return decimals ? val.toFixed(decimals) : Math.round(val).toString();
}

/**
 * AnimatedCounter — isolated component so the counter hooks live at the
 * component top-level (Rules of Hooks). Renders a single animated stat value.
 */
function AnimatedCounter({
  target,
  active,
  decimals = 0,
  suffix = '',
}: {
  target: number;
  active: boolean;
  decimals?: number;
  suffix?: string;
}): JSX.Element {
  const display = useCounter(target, active, decimals);
  return (
    <span>
      {display}
      {suffix}
    </span>
  );
}

// ── Nav Premium ────────────────────────────────────────────
function NavPremium({ scrolled }: { scrolled: boolean }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const links: { id: string; label: string }[] = [
    { id: 'features', label: 'Funcionalidades' },
    { id: 'showcase', label: 'Produto' },
    { id: 'pricing', label: 'Preços' },
  ];

  return (
    <nav
      aria-label="Navegação principal"
      style={{
        position: 'fixed',
        inset: '0 0 auto 0',
        zIndex: 100,
        height: 64,
        display: 'flex',
        alignItems: 'center',
        padding: '0 20px',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.08)' : '1px solid transparent',
        background: scrolled ? 'rgba(5,7,10,0.72)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
        transition: 'background 280ms cubic-bezier(0.16,1,0.3,1), border-color 280ms ease, backdrop-filter 280ms ease',
      }}
    >
      <div style={{ maxWidth: 1240, width: '100%', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20 }}>
        {/* Logo */}
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          aria-label="Nexora — voltar ao topo"
          style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0 }}
        >
          <span
            style={{
              width: 36, height: 36, borderRadius: 10, display: 'grid', placeItems: 'center',
              background: 'linear-gradient(135deg, #06b6d4 0%, #0ea5e9 55%, #f59e0b 100%)',
              boxShadow: '0 0 20px rgba(6,182,212,0.35), 0 2px 10px rgba(0,0,0,0.4)',
              color: '#fff', fontSize: 16, fontWeight: 800, lineHeight: 1,
            }}
            aria-hidden
          >
            ◆
          </span>
          <span
            style={{
              fontFamily: "'Sora', Inter, sans-serif",
              fontWeight: 800, fontSize: 19, letterSpacing: '-0.03em',
              background: 'linear-gradient(135deg, #fff 30%, #bae6fd 70%, #fde68a 100%)',
              WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
            }}
          >
            Nexora
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.45)', marginLeft: 2, marginTop: 4 }}>ERP</span>
        </a>

        {/* Links desktop */}
        <div className="nav-links" style={{ display: 'flex', alignItems: 'center', gap: 4 }} role="menubar">
          {links.map((l) => (
            <button
              key={l.id}
              role="menuitem"
              onClick={() => scrollTo(l.id)}
              className="nav-link-underline"
              style={{
                position: 'relative',
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#cbd5e1', fontSize: 14, fontWeight: 500, letterSpacing: '-0.01em',
                padding: '8px 14px', borderRadius: 8, transition: 'color 160ms ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#cbd5e1')}
            >
              {l.label}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {isAuthenticated ? (
            <button onClick={() => navigate({ to: '/dashboard' })} className="btn-shimmer-premium" style={ctaPrimary}>
              Painel →
            </button>
          ) : (
            <>
              <button
                onClick={() => navigate({ to: '/login' })}
                aria-label="Entrar na conta"
                style={{
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)',
                  color: '#e2e8f0', fontWeight: 600, fontSize: 14, padding: '9px 16px', borderRadius: 999,
                  cursor: 'pointer', transition: 'all 160ms ease',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.10)'; (e.currentTarget as HTMLButtonElement).style.color = '#fff'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLButtonElement).style.color = '#e2e8f0'; }}
              >
                Entrar
              </button>
              <button
                onClick={() => navigate({ to: '/login' })}
                aria-label="Começar grátis"
                className="btn-shimmer-premium"
                style={ctaPrimary}
              >
                Começar grátis
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

const ctaPrimary: React.CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #06b6d4 0%, #0ea5e9 50%, #06b6d4 100%)',
  color: '#05070a',
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: '-0.01em',
  padding: '10px 18px',
  borderRadius: 999,
  border: '1px solid rgba(255,255,255,0.18)',
  cursor: 'pointer',
  boxShadow: '0 0 24px rgba(6,182,212,0.35), 0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.35)',
  transition: 'transform 160ms cubic-bezier(0.16,1,0.3,1), box-shadow 160ms ease',
};

// ── Dashboard 3D Mock ──────────────────────────────────────
function DashboardMock3D() {
  return (
    <div
      aria-hidden
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 560,
        perspective: 1200,
        transformStyle: 'preserve-3d',
      }}
    >
      {/* Glow behind */}
      <div style={{ position: 'absolute', inset: '-20% -10% -10% -10%', background: 'radial-gradient(600px 400px at 50% 40%, rgba(6,182,212,0.18), transparent 65%), radial-gradient(500px 300px at 80% 90%, rgba(245,158,11,0.10), transparent 65%)', filter: 'blur(8px)', pointerEvents: 'none' }} />

      {/* Main card */}
      <div
        className="float-main"
        style={{
          position: 'relative',
          borderRadius: 20,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, rgba(15,23,42,0.88) 0%, rgba(8,11,18,0.92) 100%)',
          border: '1px solid rgba(255,255,255,0.10)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.55), 0 8px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08), 0 0 0 1px rgba(6,182,212,0.12)',
          transform: 'rotateY(-7deg) rotateX(5deg)',
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Window chrome */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 999, background: '#ef4444', border: '1px solid rgba(0,0,0,0.15)' }} />
            <span style={{ width: 10, height: 10, borderRadius: 999, background: '#f59e0b', border: '1px solid rgba(0,0,0,0.15)' }} />
            <span style={{ width: 10, height: 10, borderRadius: 999, background: '#10b981', border: '1px solid rgba(0,0,0,0.15)' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#64748b', fontWeight: 600 }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: '#10b981', boxShadow: '0 0 8px rgba(34,197,94,0.6)' }} /> nexora.app/dashboard
          </div>
          <div style={{ width: 28, height: 28, borderRadius: 999, background: 'linear-gradient(135deg, #06b6d4, #f59e0b)', display: 'grid', placeItems: 'center', color: '#fff', fontSize: 11, fontWeight: 800 }}>A</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '132px 1fr', minHeight: 340 }}>
          {/* Sidebar */}
          <div style={{ borderRight: '1px solid rgba(255,255,255,0.06)', padding: '14px 10px', display: 'flex', flexDirection: 'column', gap: 6, background: 'rgba(0,0,0,0.18)' }}>
            {[
              { label: 'Dashboard', active: true, icon: '◧' },
              { label: 'Vendas', icon: '◫' },
              { label: 'Estoque', icon: '⬡' },
              { label: 'Financeiro', icon: '◎' },
              { label: 'Produção', icon: '⬢' },
            ].map((it) => (
              <div key={it.label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, background: it.active ? 'rgba(6,182,212,0.14)' : 'transparent', border: it.active ? '1px solid rgba(6,182,212,0.22)' : '1px solid transparent', color: it.active ? '#06b6d4' : '#94a3b8', fontSize: 12, fontWeight: 600 }}>
                <span style={{ fontSize: 12 }}>{it.icon}</span> {it.label}
              </div>
            ))}
            <div style={{ marginTop: 'auto', padding: '10px', borderRadius: 12, background: 'linear-gradient(135deg, rgba(6,182,212,0.14), rgba(245,158,11,0.10))', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#e2e8f0' }}>Plano Profissional</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>8/10 usuários</div>
              <div style={{ height: 4, borderRadius: 999, background: 'rgba(255,255,255,0.10)', marginTop: 8, overflow: 'hidden' }}>
                <div style={{ width: '78%', height: '100%', background: 'linear-gradient(90deg, #06b6d4, #f59e0b)', borderRadius: 999 }} />
              </div>
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '14px 14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
              {[
                { k: 'Receita', v: 'R$ 128.430', d: '+12.4%', c: '#06b6d4' },
                { k: 'Pedidos', v: '1.432', d: '+8.1%', c: '#10b981' },
                { k: 'Ticket', v: 'R$ 89,60', d: '+3.2%', c: '#8b5cf6' },
                { k: 'NPS', v: '78', d: 'Excelente', c: '#f59e0b' },
              ].map((x) => (
                <div key={x.k} style={{ borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', padding: '10px 10px 9px' }}>
                  <div style={{ fontSize: 10, letterSpacing: '0.06em', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>{x.k}</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#fff', marginTop: 4, letterSpacing: '-0.02em' }}>{x.v}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: x.c, marginTop: 2 }}>{x.d}</div>
                </div>
              ))}
            </div>

            {/* Chart */}
            <div style={{ borderRadius: 14, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', padding: 12, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0' }}>Faturamento · últimos 7 dias</div>
                <div style={{ fontSize: 11, color: '#06b6d4', fontWeight: 700, background: 'rgba(6,182,212,0.12)', border: '1px solid rgba(6,182,212,0.18)', padding: '3px 8px', borderRadius: 999 }}>+14% vs semana anterior</div>
              </div>
              <svg viewBox="0 0 360 90" width="100%" height={90} preserveAspectRatio="none" role="img" aria-label="Gráfico de faturamento">
                <defs>
                  <linearGradient id="g-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="g-line" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#06b6d4" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                </defs>
                <path d="M0 62 Q40 58 70 52 T130 46 T190 34 T250 42 T310 18 T360 26 L360 90 L0 90 Z" fill="url(#g-area)" />
                <path d="M0 62 Q40 58 70 52 T130 46 T190 34 T250 42 T310 18 T360 26" fill="none" stroke="url(#g-line)" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
                {/* Dots */}
                {[70, 130, 190, 250, 310].map((x, i) => (
                  <circle key={x} cx={x} cy={[52, 46, 34, 42, 18][i]} r={3.5} fill="#fff" stroke="#06b6d4" strokeWidth={1.5} />
                ))}
                {/* Grid */}
                {[0, 30, 60].map((y) => (
                  <line key={y} x1={0} x2={360} y1={y} y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth={1} strokeDasharray="3 6" />
                ))}
              </svg>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#64748b', fontWeight: 600, marginTop: 6 }}>
                <span>SEG</span><span>TER</span><span>QUA</span><span>QUI</span><span>SEX</span><span>SÁB</span><span>DOM</span>
              </div>
            </div>

            {/* Table preview */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
              {[
                { n: '#10234 · Eletro Center', v: 'R$ 4.820', s: 'Pago' },
                { n: '#10233 · Casa Norte', v: 'R$ 1.240', s: 'Enviado' },
                { n: '#10232 · Martech Brasil', v: 'R$ 8.150', s: 'Pago' },
              ].map((r) => (
                <div key={r.n} style={{ display: 'contents' }}>
                  <div style={{ fontSize: 11, color: '#cbd5e1', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.n}</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifySelf: 'end' }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: '#fff' }}>{r.v}</span>
                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.04em', padding: '3px 7px', borderRadius: 999, background: r.s === 'Pago' ? 'rgba(16,185,129,0.14)' : 'rgba(245,158,11,0.14)', color: r.s === 'Pago' ? '#34d399' : '#f59e0b', border: `1px solid ${r.s === 'Pago' ? 'rgba(16,185,129,0.22)' : 'rgba(245,158,11,0.22)'}` }}>{r.s}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating cards */}
      <div className="float-a" style={floatingCardStyle({ top: -18, right: -14, width: 168 })}>
        <div style={floatIcon('#06b6d4')}>↗</div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Vendas hoje</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', letterSpacing: '-0.03em', marginTop: 2 }}>R$ 18.240</div>
          <div style={{ fontSize: 12, color: '#06b6d4', fontWeight: 700 }}>+22% · 47 pedidos</div>
        </div>
      </div>

      <div className="float-b" style={floatingCardStyle({ bottom: -16, left: -14, width: 186 })}>
        <div style={floatIcon('#f59e0b')}>◈</div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Estoque crítico</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', marginTop: 2 }}>3 itens abaixo do mínimo</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <span style={pill('#f59e0b')}>Repor hoje</span><span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, alignSelf: 'center' }}>· ver lista</span>
          </div>
        </div>
      </div>

      <div className="float-c" style={floatingCardStyle({ bottom: 62, right: -10, width: 150, padding: 10 })}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(16,185,129,0.14)', border: '1px solid rgba(16,185,129,0.22)', display: 'grid', placeItems: 'center', color: '#34d399', fontWeight: 800, fontSize: 12 }}>✓</span>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#fff' }}>NF-e 1.842</div>
            <div style={{ fontSize: 11, color: '#34d399', fontWeight: 700 }}>Autorizada SEFAZ</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function floatingCardStyle(pos: { top?: number; right?: number; bottom?: number; left?: number; width: number; padding?: number }): React.CSSProperties {
  return {
    position: 'absolute',
    ...pos,
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    padding: pos.padding ?? 12,
    borderRadius: 16,
    background: 'rgba(15,23,42,0.86)',
    backdropFilter: 'blur(16px) saturate(160%)',
    WebkitBackdropFilter: 'blur(16px) saturate(160%)',
    border: '1px solid rgba(255,255,255,0.10)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.45), 0 2px 10px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.06)',
    zIndex: 2,
  } as React.CSSProperties;
}
function floatIcon(color: string): React.CSSProperties {
  return {
    width: 36, height: 36, borderRadius: 10, display: 'grid', placeItems: 'center',
    background: `${color}18`, border: `1px solid ${color}30`, color, fontWeight: 800, fontSize: 14, flexShrink: 0,
  };
}
function pill(color: string): React.CSSProperties {
  return { fontSize: 11, fontWeight: 800, color: '#fff', background: color, padding: '3px 8px', borderRadius: 999, letterSpacing: '0.02em' };
}

// ── Data ───────────────────────────────────────────────────
const FEATURES: { title: string; desc: string; icon: string; gradient: string; span?: string }[] = [
  { title: 'PDV ultrarrápido', desc: 'Venda em 3 cliques, offline-first, impressão térmica e TEF. Fila nunca mais.', icon: '◫', gradient: 'linear-gradient(135deg, #06b6d4, #0ea5e9)', span: 'span 1' },
  { title: 'Estoque inteligente', desc: 'Multi-depósito, lote/validade, inventário por coletor e alertas preditivos de ruptura.', icon: '⬢', gradient: 'linear-gradient(135deg, #10b981, #34d399)' },
  { title: 'Financeiro completo', desc: 'Fluxo de caixa, DRE, conciliação bancária automática e boletos PIX integrados.', icon: '◎', gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' },
  { title: 'NF-e | NFC-e | NFS-e', desc: 'Emissão em lote, validação SEFAZ, carta de correção e armazenamento 5 anos.', icon: '✦', gradient: 'linear-gradient(135deg, #f59e0b, #f59e0b)' },
  { title: 'Produção & Ordens', desc: 'Ficha técnica, ordem de produção, apontamento e custo real por produto.', icon: '⬡', gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', span: 'span 1' },
  { title: 'Relatórios & IA', desc: 'Dashboards vivos, curva ABC, previsão de demanda e insights acionáveis.', icon: '◧', gradient: 'linear-gradient(135deg, #06b6d4, #8b5cf6)' },
];

const SHOWCASE_TABS = [
  { id: 'vendas', label: 'Vendas & PDV', title: 'Venda 3× mais rápido', desc: 'PDV com leitor, balança, TEF e modo offline. Devolução, desconto e crediário em 1 clique. Feche o caixa em 10 segundos.', bullets: ['TEF & PIX integrados', 'Modo offline que sincroniza', 'Fechamento em 1 clique'] },
  { id: 'estoque', label: 'Estoque', title: 'Zero ruptura', desc: 'Saldo por depósito, reserva de venda, inventário cego e curva ABC. Saiba o que repor antes de acabar.', bullets: ['Alertas preditivos', 'Lote, validade e série', 'Inventário por celular'] },
  { id: 'financeiro', label: 'Financeiro', title: 'Caixa no azul', desc: 'Contas a pagar/receber, conciliação bancária, DRE gerencial e fluxo de caixa projetado 90 dias.', bullets: ['Conciliação automática', 'Boleto PIX e CNAB', 'DRE por centro de custo'] },
  { id: 'relatorios', label: 'Relatórios', title: 'Decisão com dados', desc: 'Mais de 40 relatórios vivos, exportação e compartilhamento. Previsão de demanda com IA.', bullets: ['Dashboard customizável', 'Exporta Excel/PDF', 'Previsão de demanda'] },
] as const;

const TESTIMONIALS = [
  { name: 'Ana Silva', role: 'CEO · Martech Brasil · 42 lojas', photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=96&h=96&fit=crop&crop=face', text: 'Reduzimos o fechamento de 5 dias para 2 horas. O DRE sai sozinho e a conciliação é automática. Nexora pagou-se no primeiro mês.', rating: 5 },
  { name: 'Carlos Mendes', role: 'Diretor · Distribuidora RM · 18 depósitos', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=96&h=96&fit=crop&crop=face', text: 'Ruptura caiu 40% com os alertas preditivos. Inventário que levava 2 dias agora fazemos em 3 horas com o celular.', rating: 5 },
  { name: 'Juliana Costa', role: 'CFO · TechSolve · 120 colaboradores', photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=96&h=96&fit=crop&crop=face', text: 'Migração sem dor: trouxeram tudo do antigo ERP em 1 semana. Suporte responde em minutos, não em dias.', rating: 5 },
  { name: 'Rafael Oliveira', role: 'Sócio · Oliveira & Cia · Varejo', photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=96&h=96&fit=crop&crop=face', text: 'PDV não trava nem sem internet. Fila andou 2× mais rápido. Treinamos a equipe em uma tarde.', rating: 5 },
];

const PLANS: { name: string; monthly: number; desc: string; features: string[]; featured?: boolean; cta: string }[] = [
  { name: 'Essencial', monthly: 97, desc: 'Para quem está organizando a casa e precisa de base sólida.', features: ['Até 3 usuários', 'PDV + Estoque + NF-e', 'Relatórios essenciais', 'Suporte por e-mail (24h)', '1 depósito · 1 empresa'], cta: 'Começar no Essencial' },
  { name: 'Profissional', monthly: 197, desc: 'O queridinho: para empresas em crescimento acelerado.', features: ['Até 10 usuários', 'Tudo do Essencial +', 'Financeiro completo + conciliação', 'Boleto PIX & CNAB', 'Multi-depósito · API', 'Suporte prioritário (4h)'], featured: true, cta: 'Assinar Profissional' },
  { name: 'Enterprise', monthly: 397, desc: 'Operação complexa, multi-empresa e SLAs críticos.', features: ['Usuários ilimitados', 'Tudo do Profissional +', 'Produção & Ordens + ficha técnica', 'Multi-empresa & multi-filial', 'Gerente de conta dedicado', 'SLA 99,95% · Onboarding assistido'], cta: 'Falar com vendas' },
];

// ── Main ───────────────────────────────────────────────────
export function LandingPage(): JSX.Element {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const scrolled = useScrollScrolled(16);
  const [mounted, setMounted] = useState(false);
  const [billingAnnual, setBillingAnnual] = useState(true);
  const [activeTab, setActiveTab] = useState<(typeof SHOWCASE_TABS)[number]['id']>('vendas');
  const [tIndex, setTIndex] = useState(0);
  const [email, setEmail] = useState('');
  const [emailOk, setEmailOk] = useState(false);
  const [statsInView, setStatsInView] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (isAuthenticated && !isLoading) navigate({ to: '/dashboard', replace: true }).catch(console.error);
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e?.isIntersecting) { setStatsInView(true); obs.disconnect(); } }, { threshold: 0.35 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // carousel autoplay
  useEffect(() => {
    const id = window.setInterval(() => setTIndex((i) => (i + 1) % TESTIMONIALS.length), 5200);
    return () => window.clearInterval(id);
  }, []);

  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleNewsletter = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes('@')) return;
    setEmailOk(true);
    setTimeout(() => { setEmailOk(false); setEmail(''); }, 2600);
  }, [email]);

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#05070a' }}>
        <div style={{ width: 36, height: 36, borderRadius: 999, border: '2px solid rgba(6,182,212,0.18)', borderTopColor: '#06b6d4', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  const priceFor = (monthly: number) => billingAnnual ? Math.round(monthly * 0.8) : monthly;

  return (
    <div
      id="top"
      style={{
        position: 'relative',
        minHeight: '100vh',
        background: '#05070a',
        color: '#e2e8f0',
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        overflow: 'clip',
        opacity: mounted ? 1 : 0,
        transition: 'opacity 320ms ease',
      }}
    >
      {/* Skip link */}
      <a href="#main" style={{ position: 'absolute', left: -9999, top: 8, background: '#fff', color: '#05070a', padding: '8px 12px', borderRadius: 8, fontWeight: 700, zIndex: 999 }} onFocus={(e) => (e.currentTarget.style.left = '12px')} onBlur={(e) => (e.currentTarget.style.left = '-9999px')}>Pular para o conteúdo</a>

      <AmbientBackground subtle={false} primaryColor="6,182,212" secondaryColor="14,165,233" />

      <NavPremium scrolled={scrolled} />

      <main id="main">
        {/* ── HERO ── */}
        <section
          aria-label="Apresentação Nexora"
          style={{
            position: 'relative', zIndex: 1, maxWidth: 1240, margin: '0 auto',
            padding: '96px 20px 32px', display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: 32, alignItems: 'center', minHeight: 'min(860px, 92vh)',
          }}
          className="hero-grid"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 12 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, alignSelf: 'flex-start', padding: '6px 10px 6px 6px', borderRadius: 999, background: 'rgba(6,182,212,0.10)', border: '1px solid rgba(6,182,212,0.22)', backdropFilter: 'blur(10px)', fontSize: 12, fontWeight: 700, letterSpacing: '0.02em', color: '#06b6d4' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 999, background: 'rgba(6,182,212,0.18)', border: '1px solid rgba(6,182,212,0.22)', color: '#fff', fontSize: 11, fontWeight: 800, letterSpacing: '0.04em' }}>★ NOVO</span>
              NF-e 4.0 + PIX automático — já disponível
            </div>

            <h1
              style={{
                fontFamily: "'Sora', Inter, sans-serif",
                fontWeight: 800,
                fontSize: 'clamp(36px, 5.2vw, 62px)',
                lineHeight: 0.95,
                letterSpacing: '-0.04em',
                color: '#fff',
                margin: 0,
              }}
            >
              Gestão
              <br />
              <span style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0ea5e9 45%, #06b6d4 75%, #f59e0b 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>inteligente</span>
              <br />
              <span style={{ color: '#fff' }}>para o Brasil real</span>
            </h1>

            <p style={{ fontSize: 'clamp(15px, 1.5vw, 17px)', lineHeight: 1.65, color: '#94a3b8', maxWidth: 560, margin: 0 }}>
              Vendas, estoque, financeiro e produção em um sistema único, rápido e seguro. Do balcão à indústria — sem planilhas, sem sustos, com <strong style={{ color: '#e2e8f0', fontWeight: 700 }}>14 dias grátis</strong>.
            </p>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
              <button onClick={() => navigate({ to: '/login' })} className="btn-shimmer-premium" style={{ ...ctaPrimary, padding: '13px 20px', fontSize: 15 }}>
                Começar grátis — 14 dias
                <span aria-hidden style={{ marginLeft: 6 }}>→</span>
              </button>
              <button
                onClick={() => scrollTo('features')}
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#e2e8f0', fontWeight: 700, fontSize: 14, padding: '13px 18px', borderRadius: 999, cursor: 'pointer', backdropFilter: 'blur(10px)', transition: 'all 160ms ease' }}
                aria-label="Ver demonstração e funcionalidades"
              >
                ▶ Ver demonstração
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {[
                  'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80&h=80&fit=crop&crop=face',
                  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face',
                  'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&h=80&fit=crop&crop=face',
                  'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face',
                ].map((src, i) => (
                  <img key={src} src={src} alt="" width={32} height={32} style={{ width: 32, height: 32, borderRadius: 999, border: '2px solid #05070a', marginLeft: i ? -8 : 0, objectFit: 'cover' }} loading="lazy" />
                ))}
                <span style={{ width: 32, height: 32, borderRadius: 999, background: 'linear-gradient(135deg, #06b6d4, #f59e0b)', border: '2px solid #05070a', marginLeft: -8, display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 800, fontSize: 11 }}>+500</span>
              </div>
              <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>
                <span style={{ color: '#f59e0b' }}>★★★★★</span> 4,9/5 · <strong style={{ color: '#e2e8f0' }}>500+</strong> empresas confiam
              </div>
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, borderLeft: '1px solid rgba(255,255,255,0.10)', paddingLeft: 12 }}>Sem cartão · Cancele quando quiser</span>
            </div>

            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 8, flexWrap: 'wrap', fontSize: 12, color: '#64748b', fontWeight: 600 }}>
              <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><span style={{ width: 6, height: 6, borderRadius: 999, background: '#10b981', boxShadow: '0 0 10px rgba(16,185,129,0.5)' }} /> LGPD & Criptografia</span>
              <span>·</span>
              <span>MFA · Auditoria</span>
              <span>·</span>
              <span>Suporte em minutos</span>
            </div>
          </div>

          <div style={{ display: 'grid', placeItems: 'center', padding: '10px 0' }}>
            <DashboardMock3D />
          </div>
        </section>

        {/* ── TRUST STRIP + STATS ── */}
        <section aria-label="Confiança e métricas" style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.00))', backdropFilter: 'blur(10px)' }}>
          <div style={{ maxWidth: 1240, margin: '0 auto', padding: '14px 20px 0', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
            <span style={{ fontSize: 11, letterSpacing: '0.12em', fontWeight: 800, color: 'rgba(255,255,255,0.42)', textTransform: 'uppercase' }}>Confiado por varejo, distribuição e indústria</span>
            <span style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.10)', margin: '0 4px' }} aria-hidden />
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', fontWeight: 800, letterSpacing: '-0.02em', fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
              <span>ELETRO CENTER</span><span>·</span><span>CASA NORTE</span><span>·</span><span>MARTECH</span><span>·</span><span>RM DISTRIBUIDORA</span><span>·</span><span>TECHSOLVE</span>
            </div>
          </div>
          <div ref={statsRef} style={{ maxWidth: 1240, margin: '0 auto', padding: '16px 20px 18px', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }} className="stats-grid">
            {[
              { label: 'Empresas ativas', suffix: '+', value: 500, decimals: 0 },
              { label: 'Transações / mês', suffix: 'K+', value: 50, decimals: 0 },
              { label: 'Uptime SLA', suffix: '%', value: 99.9, decimals: 1 },
              { label: 'NF-e / mês', suffix: 'K+', value: 15, decimals: 0 },
            ].map((s) => (
              <div key={s.label} style={{ borderRadius: 16, background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', padding: '14px 14px 12px', textAlign: 'center' }}>
                <div style={{ width: 8, height: 8, borderRadius: 999, background: '#06b6d4', boxShadow: '0 0 14px rgba(6,182,212,0.5)', margin: '0 auto 8px' }} aria-hidden />
                <div style={{ fontFamily: "'Sora', Inter, sans-serif", fontWeight: 800, fontSize: 'clamp(20px, 2.6vw, 28px)', letterSpacing: '-0.04em', color: '#fff' }}>
                  <AnimatedCounter target={s.value} active={statsInView} decimals={s.decimals} suffix={s.suffix} />
                </div>
                <div style={{ fontSize: 11, letterSpacing: '0.06em', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── FEATURES BENTO 6 ── */}
        <section id="features" aria-label="Funcionalidades" style={{ position: 'relative', zIndex: 1, maxWidth: 1240, margin: '0 auto', padding: '56px 20px 28px' }}>
          <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 22px' }}>
            <div style={badgeStyle}>FUNCIONALIDADES</div>
            <h2 style={h2Style}>Tudo que sua operação precisa — <span style={{ background: 'linear-gradient(135deg, #06b6d4, #06b6d4)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>sem gambiarra</span></h2>
            <p style={subStyle}>Módulos que conversam entre si. Dados únicos, sem retrabalho. Do PDV ao DRE em um só lugar.</p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(12, 1fr)',
              gap: 12,
              gridAutoRows: 'minmax(180px, auto)',
            }}
            className="bento-grid"
          >
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="bento-card"
                onMouseMove={(e) => {
                  const el = e.currentTarget as HTMLDivElement;
                  const r = el.getBoundingClientRect();
                  el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
                  el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
                }}
                style={{
                  gridColumn: i === 0 || i === 4 ? 'span 7' : i === 1 || i === 5 ? 'span 5' : 'span 6',
                  borderRadius: 20,
                  background: 'rgba(15,23,42,0.55)',
                  backdropFilter: 'blur(16px) saturate(160%)',
                  WebkitBackdropFilter: 'blur(16px) saturate(160%)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)',
                  padding: 18,
                  display: 'flex', flexDirection: 'column', gap: 12,
                  position: 'relative', overflow: 'hidden',
                  transition: 'transform 260ms cubic-bezier(0.16,1,0.3,1), border-color 260ms ease, box-shadow 260ms ease',
                }}
              >
                <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(500px 300px at var(--mx,50%) var(--my,30%), rgba(6,182,212,0.10), transparent 60%)', opacity: 0, transition: 'opacity 260ms ease', pointerEvents: 'none' }} className="bento-glow" />
                <div style={{ width: 44, height: 44, borderRadius: 12, display: 'grid', placeItems: 'center', background: f.gradient, color: '#fff', fontWeight: 800, fontSize: 16, boxShadow: '0 8px 20px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.10) inset', flexShrink: 0 }}>{f.icon}</div>
                <h3 style={{ margin: 0, fontFamily: "'Sora', Inter, sans-serif", fontWeight: 700, fontSize: 16, letterSpacing: '-0.02em', color: '#fff' }}>{f.title}</h3>
                <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: '#94a3b8' }}>{f.desc}</p>
                <div style={{ marginTop: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#06b6d4', background: 'rgba(6,182,212,0.10)', border: '1px solid rgba(6,182,212,0.18)', padding: '4px 8px', borderRadius: 999 }}>Ver no produto →</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── SHOWCASE TABS ── */}
        <section id="showcase" aria-label="Demonstração do produto" style={{ position: 'relative', zIndex: 1, maxWidth: 1240, margin: '0 auto', padding: '36px 20px 28px' }}>
          <div style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            <div>
              <div style={badgeStyle}>DEMONSTRAÇÃO</div>
              <h2 style={{ ...h2Style, textAlign: 'left', margin: '8px 0 6px' }}>Veja o Nexora em ação</h2>
              <p style={{ ...subStyle, textAlign: 'left', margin: 0 }}>Troque de módulo sem perder contexto. Dados únicos, navegação instantânea.</p>
            </div>
            <div style={{ display: 'flex', gap: 6, padding: 4, borderRadius: 999, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }} role="tablist" aria-label="Módulos">
              {SHOWCASE_TABS.map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={activeTab === t.id}
                  onClick={() => setActiveTab(t.id)}
                  style={{
                    padding: '8px 14px', borderRadius: 999, border: '1px solid transparent', cursor: 'pointer',
                    background: activeTab === t.id ? 'linear-gradient(135deg, #06b6d4, #0ea5e9)' : 'transparent',
                    color: activeTab === t.id ? '#05070a' : '#cbd5e1', fontWeight: 700, fontSize: 13,
                    boxShadow: activeTab === t.id ? '0 4px 16px rgba(6,182,212,0.30)' : 'none',
                    transition: 'all 160ms ease',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {(() => {
            const tab = SHOWCASE_TABS.find((x) => x.id === activeTab) ?? SHOWCASE_TABS[0]!;
            return (
              <div style={{ display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', gap: 16, alignItems: 'stretch' }} className="showcase-grid">
                <div style={{ borderRadius: 20, background: 'rgba(15,23,42,0.55)', border: '1px solid rgba(255,255,255,0.08)', padding: 20, backdropFilter: 'blur(16px)', display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <h3 style={{ margin: 0, fontFamily: "'Sora', Inter, sans-serif", fontWeight: 800, fontSize: 22, letterSpacing: '-0.03em', color: '#fff' }}>{tab.title}</h3>
                  <p style={{ margin: 0, color: '#94a3b8', lineHeight: 1.65, fontSize: 14 }}>{tab.desc}</p>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}>
                    {tab.bullets.map((b) => (
                      <li key={b} style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#e2e8f0', fontWeight: 600, fontSize: 13 }}>
                        <span style={{ width: 22, height: 22, borderRadius: 999, display: 'grid', placeItems: 'center', background: 'rgba(6,182,212,0.14)', border: '1px solid rgba(6,182,212,0.22)', color: '#06b6d4', fontSize: 12, fontWeight: 800 }}>✓</span> {b}
                      </li>
                    ))}
                  </ul>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button onClick={() => navigate({ to: '/login' })} style={{ ...ctaPrimary, padding: '10px 16px' }}>Testar agora →</button>
                    <button onClick={() => scrollTo('pricing')} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#e2e8f0', fontWeight: 700, fontSize: 13, padding: '10px 14px', borderRadius: 999, cursor: 'pointer' }}>Ver preços</button>
                  </div>
                </div>

                <div style={{ borderRadius: 20, overflow: 'hidden', background: 'linear-gradient(180deg, rgba(15,23,42,0.72), rgba(8,11,18,0.88))', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 16px 48px rgba(0,0,0,0.45)', display: 'grid', placeItems: 'center', padding: 16, minHeight: 320, position: 'relative' }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(500px 300px at 30% 20%, rgba(6,182,212,0.14), transparent 60%), radial-gradient(400px 300px at 90% 80%, rgba(245,158,11,0.08), transparent 60%)', pointerEvents: 'none' }} />
                  {/* Tab visuals */}
                  {activeTab === 'vendas' && (
                    <div style={{ position: 'relative', width: '100%', maxWidth: 420, borderRadius: 16, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', padding: 12, display: 'grid', gap: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, fontWeight: 800, color: '#e2e8f0' }}><span>PDV — Caixa 01</span><span style={{ background: '#10b981', color: '#fff', padding: '4px 8px', borderRadius: 999, fontSize: 11 }}>ABERTO</span></div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 10 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }}>
                          {['Café 250g', 'Arroz 5kg', 'Óleo 900ml', 'Sabão 1kg', 'Leite 1L', 'Pão 500g'].map((p) => (
                            <div key={p} style={{ borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', padding: '10px 8px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#cbd5e1' }}>{p}<div style={{ color: '#06b6d4', marginTop: 4 }}>R$ {(Math.random() * 20 + 5).toFixed(2)}</div></div>
                          ))}
                        </div>
                        <div style={{ borderRadius: 12, background: '#05070a', border: '1px solid rgba(255,255,255,0.06)', padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', letterSpacing: '-0.03em' }}>R$ 87,40</div>
                          <div style={{ height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #06b6d4, #0ea5e9)', display: 'grid', placeItems: 'center', color: '#05070a', fontWeight: 800, fontSize: 12 }}>PAGAR →</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>PIX · Cartão · Dinheiro</div>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === 'estoque' && (
                    <div style={{ position: 'relative', width: '100%', maxWidth: 440, display: 'grid', gap: 10 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                        {[
                          { n: 'Café 250g', q: '12 un', s: 'ok' },
                          { n: 'Arroz 5kg', q: '3 un', s: 'warn' },
                          { n: 'Óleo 900ml', q: '0 un', s: 'crit' },
                        ].map((it) => (
                          <div key={it.n} style={{ borderRadius: 12, padding: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', borderLeft: `3px solid ${it.s === 'ok' ? '#10b981' : it.s === 'warn' ? '#f59e0b' : '#ef4444'}` }}>
                            <div style={{ fontSize: 12, fontWeight: 800, color: '#fff' }}>{it.n}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{it.q} · Dep. Central</div>
                            <div style={{ marginTop: 6, fontSize: 11, fontWeight: 800, color: it.s === 'ok' ? '#34d399' : it.s === 'warn' ? '#f59e0b' : '#f87171' }}>{it.s === 'ok' ? '✓ OK' : it.s === 'warn' ? '⚠ Repor' : '✕ Crítico'}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ borderRadius: 12, background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.18)', padding: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#fde68a' }}>Sugestão de compra gerada · 12 itens</span>
                        <span style={{ background: '#f59e0b', color: '#fff', fontWeight: 800, fontSize: 11, padding: '6px 10px', borderRadius: 999 }}>Ver pedido</span>
                      </div>
                    </div>
                  )}
                  {activeTab === 'financeiro' && (
                    <div style={{ position: 'relative', width: '100%', maxWidth: 420, borderRadius: 16, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', padding: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 800, color: '#fff', fontSize: 13 }}>Fluxo de caixa · 90 dias</span>
                        <span style={{ fontSize: 11, color: '#34d399', fontWeight: 800, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.22)', padding: '4px 8px', borderRadius: 999 }}>+R$ 42.180 projetado</span>
                      </div>
                      <svg viewBox="0 0 320 80" width="100%" height={80} style={{ marginTop: 10 }}>
                        <path d="M0 58 Q40 54 80 48 T160 36 T240 30 T320 18" fill="none" stroke="#06b6d4" strokeWidth={2.2} />
                        <path d="M0 58 Q40 54 80 48 T160 36 T240 30 T320 18 L320 80 L0 80 Z" fill="rgba(6,182,212,0.14)" />
                      </svg>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
                        <div style={kpiMini}>A pagar <strong>R$ 18.420</strong></div>
                        <div style={kpiMini}>A receber <strong>R$ 60.600</strong></div>
                      </div>
                    </div>
                  )}
                  {activeTab === 'relatorios' && (
                    <div style={{ position: 'relative', width: '100%', maxWidth: 420, display: 'grid', gap: 10 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div style={{ borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', padding: 12 }}>
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Curva ABC</div>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'end', marginTop: 8, height: 48 }}>
                            <div style={{ flex: 2, height: '78%', background: '#06b6d4', borderRadius: 6 }} />
                            <div style={{ flex: 1.2, height: '46%', background: '#f59e0b', borderRadius: 6 }} />
                            <div style={{ flex: 0.8, height: '28%', background: 'rgba(255,255,255,0.18)', borderRadius: 6 }} />
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>A 78% · B 16% · C 6%</div>
                        </div>
                        <div style={{ borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', padding: 12 }}>
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Previsão IA</div>
                          <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginTop: 6 }}>+18%</div>
                          <div style={{ fontSize: 12, color: '#06b6d4', fontWeight: 700 }}>Demanda próx. 30 dias</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Base: 12 meses · sazonalidade</div>
                        </div>
                      </div>
                      <div style={{ borderRadius: 12, background: 'rgba(6,182,212,0.08)', border: '1px solid rgba(6,182,212,0.16)', padding: 10, fontSize: 12, color: '#bae6fd', fontWeight: 600 }}>↗ Insight: aumente estoque de “Café 250g” em 22% para não perder venda no fim de mês.</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </section>

        {/* ── TESTIMONIALS CAROUSEL ── */}
        <section aria-label="Depoimentos" style={{ position: 'relative', zIndex: 1, maxWidth: 1240, margin: '0 auto', padding: '18px 20px 28px' }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={badgeStyle}>DEPOIMENTOS</div>
            <h2 style={h2Style}>Quem usa, recomenda — e fica</h2>
            <p style={subStyle}>Histórias reais de quem trocou planilha e sistema antigo por Nexora.</p>
          </div>

          <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 20, background: 'rgba(15,23,42,0.45)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(16px)', padding: '18px 14px 14px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 100%)', transform: `translateX(-${tIndex * 100}%)`, transition: 'transform 520ms cubic-bezier(0.16,1,0.3,1)' }} role="list">
              {TESTIMONIALS.map((t) => (
                <div key={t.name} role="listitem" style={{ padding: '0 8px', display: 'grid', gridTemplateColumns: '1fr', gap: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14 }} className="testimonial-slide">
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <img src={t.photo} alt={`Foto de ${t.name}`} width={48} height={48} style={{ width: 48, height: 48, borderRadius: 999, objectFit: 'cover', border: '2px solid rgba(255,255,255,0.10)' }} loading="lazy" />
                      <div>
                        <div style={{ fontWeight: 800, color: '#fff', fontSize: 14 }}>{t.name}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{t.role}</div>
                      </div>
                      <div style={{ marginLeft: 'auto', color: '#f59e0b', letterSpacing: 1, fontSize: 12 }} aria-label={`Avaliação ${t.rating} de 5`}>{'★'.repeat(t.rating)}</div>
                    </div>
                    <blockquote style={{ margin: 0, fontSize: 15, lineHeight: 1.65, color: '#e2e8f0', fontWeight: 500, letterSpacing: '-0.01em' }}>“{t.text}”</blockquote>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, gap: 12 }}>
              <div style={{ display: 'flex', gap: 6 }} role="tablist" aria-label="Trocar depoimento">
                {TESTIMONIALS.map((_, i) => (
                  <button key={i} aria-label={`Ir para depoimento ${i + 1}`} aria-selected={tIndex === i} onClick={() => setTIndex(i)} style={{ width: tIndex === i ? 22 : 8, height: 8, borderRadius: 999, border: 'none', cursor: 'pointer', background: tIndex === i ? '#06b6d4' : 'rgba(255,255,255,0.18)', transition: 'all 180ms ease' }} />
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button aria-label="Depoimento anterior" onClick={() => setTIndex((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)} style={carouselArrow}>‹</button>
                <button aria-label="Próximo depoimento" onClick={() => setTIndex((i) => (i + 1) % TESTIMONIALS.length)} style={carouselArrow}>›</button>
              </div>
            </div>
          </div>
        </section>

        {/* ── PRICING ── */}
        <section id="pricing" aria-label="Planos e preços" style={{ position: 'relative', zIndex: 1, maxWidth: 1240, margin: '0 auto', padding: '22px 20px 28px' }}>
          <div style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 18px' }}>
            <div style={badgeStyle}>PREÇOS TRANSPARENTES</div>
            <h2 style={h2Style}>Comece grátis. Evolua sem sustos.</h2>
            <p style={subStyle}>Sem fidelidade. Cancele quando quiser. Migração assistida inclusa.</p>

            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 14, padding: 4, borderRadius: 999, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }} role="group" aria-label="Periodicidade">
              <button onClick={() => setBillingAnnual(false)} aria-pressed={!billingAnnual} style={{ padding: '8px 14px', borderRadius: 999, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 13, background: !billingAnnual ? '#fff' : 'transparent', color: !billingAnnual ? '#05070a' : '#cbd5e1' }}>Mensal</button>
              <button onClick={() => setBillingAnnual(true)} aria-pressed={billingAnnual} style={{ padding: '8px 14px', borderRadius: 999, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 13, background: billingAnnual ? 'linear-gradient(135deg, #06b6d4, #f59e0b)' : 'transparent', color: billingAnnual ? '#05070a' : '#cbd5e1', boxShadow: billingAnnual ? '0 4px 16px rgba(6,182,212,0.25)' : 'none' }}>Anual <span style={{ fontWeight: 800, background: 'rgba(255,255,255,0.85)', color: '#065f46', padding: '2px 6px', borderRadius: 999, marginLeft: 6, fontSize: 11 }}>−20%</span></button>
            </div>
            {billingAnnual && <div style={{ fontSize: 12, color: '#34d399', fontWeight: 700, marginTop: 8 }}>Economize até R$ 960/ano no Profissional</div>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, alignItems: 'stretch' }} className="pricing-grid">
            {PLANS.map((p) => (
              <div
                key={p.name}
                style={{
                  position: 'relative',
                  borderRadius: 20,
                  padding: 18,
                  background: p.featured ? 'linear-gradient(180deg, rgba(15,23,42,0.85), rgba(8,11,18,0.92))' : 'rgba(15,23,42,0.55)',
                  border: p.featured ? '1px solid rgba(245,158,11,0.35)' : '1px solid rgba(255,255,255,0.08)',
                  boxShadow: p.featured ? '0 0 0 1px rgba(245,158,11,0.12), 0 16px 48px rgba(245,158,11,0.12), 0 8px 32px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.08)' : '0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                  transform: p.featured ? 'translateY(-4px)' : 'none',
                  display: 'flex', flexDirection: 'column', gap: 12,
                  overflow: 'hidden',
                }}
              >
                {p.featured && (
                  <div style={{ position: 'absolute', top: 12, right: 12, background: 'linear-gradient(135deg, #f59e0b, #f59e0b)', color: '#1a1200', fontWeight: 800, fontSize: 11, letterSpacing: '0.06em', padding: '5px 10px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.35)', boxShadow: '0 4px 16px rgba(245,158,11,0.30)' }}>MAIS POPULAR</div>
                )}
                <div>
                  <div style={{ fontFamily: "'Sora', Inter, sans-serif", fontWeight: 800, fontSize: 18, color: '#fff', letterSpacing: '-0.02em' }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, lineHeight: 1.5 }}>{p.desc}</div>
                </div>

                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 2 }}>
                  <span style={{ fontSize: 13, color: '#64748b', fontWeight: 700 }}>R$</span>
                  <span style={{ fontFamily: "'Sora', Inter, sans-serif", fontWeight: 800, fontSize: 36, letterSpacing: '-0.04em', color: '#fff', lineHeight: 1 }}>{priceFor(p.monthly)}</span>
                  <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>/mês {billingAnnual ? 'no anual' : 'no mensal'}</span>
                </div>
                {billingAnnual && <div style={{ fontSize: 11, color: '#64748b' }}>Cobrado anualmente · <span style={{ color: '#34d399', fontWeight: 700 }}>2 meses grátis</span></div>}

                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 8, marginTop: 4 }}>
                  {p.features.map((f) => (
                    <li key={f} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: '#cbd5e1', lineHeight: 1.4 }}>
                      <span style={{ width: 18, height: 18, borderRadius: 999, display: 'grid', placeItems: 'center', background: 'rgba(6,182,212,0.12)', border: '1px solid rgba(6,182,212,0.18)', color: '#06b6d4', fontSize: 10, fontWeight: 800, flexShrink: 0, marginTop: 1 }}>✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate({ to: '/login' })}
                  style={{
                    marginTop: 'auto',
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: 999,
                    border: p.featured ? '1px solid rgba(255,255,255,0.22)' : '1px solid rgba(255,255,255,0.10)',
                    cursor: 'pointer',
                    fontWeight: 800, fontSize: 14,
                    background: p.featured ? 'linear-gradient(135deg, #f59e0b 0%, #f59e0b 50%, #fde68a 100%)' : 'rgba(255,255,255,0.06)',
                    color: p.featured ? '#1a1200' : '#fff',
                    boxShadow: p.featured ? '0 8px 20px rgba(245,158,11,0.28)' : 'none',
                    transition: 'transform 160ms ease, box-shadow 160ms ease',
                  }}
                >
                  {p.cta} →
                </button>
                <div style={{ fontSize: 11, color: '#64748b', textAlign: 'center', fontWeight: 600 }}>14 dias grátis · sem cartão</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 14, borderRadius: 16, background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', padding: 14, display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>Dúvidas? Fale com vendas no WhatsApp</span>
            <a href="#" onClick={(e) => e.preventDefault()} style={{ fontSize: 13, fontWeight: 800, color: '#06b6d4', textDecoration: 'none', background: 'rgba(6,182,212,0.10)', border: '1px solid rgba(6,182,212,0.18)', padding: '6px 10px', borderRadius: 999 }}>(11) 99999-0000 — resposta em minutos</a>
            <span style={{ fontSize: 12, color: '#64748b' }}>· Segunda a sábado, 8h–20h</span>
          </div>
        </section>

        {/* ── TECH TRUST ── */}
        <section aria-label="Tecnologia" style={{ position: 'relative', zIndex: 1, maxWidth: 1240, margin: '0 auto', padding: '8px 20px 22px' }}>
          <div style={{ borderRadius: 20, background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', padding: '16px 16px', display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 11, letterSpacing: '0.10em', fontWeight: 800, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase' }}>Stack confiável</span>
              <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  ['React', '#06b6d4'],
                  ['TypeScript', '#0ea5e9'],
                  ['Node.js', '#10b981'],
                  ['PostgreSQL', '#0ea5e9'],
                  ['Redis', '#ef4444'],
                  ['Docker', '#0ea5e9'],
                  ['AWS', '#f59e0b'],
                  ['LGPD', '#10b981'],
                ].map(([name, color]) => (
                  <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 999, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', fontSize: 12, fontWeight: 700, color: '#cbd5e1' }}>
                    <span style={{ width: 8, height: 8, borderRadius: 999, background: color as string, boxShadow: `0 0 10px ${color}60` }} aria-hidden /> {name}
                  </span>
                ))}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', fontSize: 12, color: '#64748b', fontWeight: 600 }}>
              <span>✓ Criptografia em repouso e em trânsito</span><span>·</span><span>✓ Backups diários + PITR</span><span>·</span><span>✓ Auditoria completa de ações</span><span>·</span><span>✓ MFA e controle por papéis</span>
            </div>
          </div>
        </section>

        {/* ── CTA GRADIENT ── */}
        <section aria-label="Chamada final" style={{ position: 'relative', zIndex: 1, maxWidth: 1240, margin: '0 auto', padding: '0 20px 28px' }}>
          <div style={{ position: 'relative', borderRadius: 24, overflow: 'hidden', padding: '28px 20px', background: 'linear-gradient(135deg, #06b6d4 0%, #0ea5e9 42%, #8b5cf6 72%, #f59e0b 100%)', boxShadow: '0 16px 48px rgba(6,182,212,0.25), 0 8px 24px rgba(0,0,0,0.35)', isolation: 'isolate' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(700px 400px at 20% 10%, rgba(255,255,255,0.18), transparent 60%), radial-gradient(600px 300px at 90% 90%, rgba(255,255,255,0.12), transparent 60%)', pointerEvents: 'none' }} />
            <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: '1.2fr auto', gap: 16, alignItems: 'center' }} className="cta-grid">
              <div>
                <h2 style={{ margin: 0, fontFamily: "'Sora', Inter, sans-serif", fontWeight: 800, fontSize: 'clamp(22px, 2.8vw, 30px)', letterSpacing: '-0.03em', color: '#05070a', lineHeight: 1.1 }}>Pronto para transformar sua gestão?</h2>
                <p style={{ margin: '8px 0 0', color: 'rgba(0,16,24,0.72)', fontWeight: 600, fontSize: 14, lineHeight: 1.5 }}>Comece grátis hoje. Migração assistida, treinamento e suporte que responde em minutos — não em dias.</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, fontSize: 12, fontWeight: 700, color: 'rgba(0,16,24,0.70)' }}>
                  <span style={{ background: 'rgba(255,255,255,0.85)', padding: '5px 10px', borderRadius: 999 }}>✓ 14 dias grátis</span>
                  <span style={{ background: 'rgba(255,255,255,0.85)', padding: '5px 10px', borderRadius: 999 }}>✓ Sem cartão</span>
                  <span style={{ background: 'rgba(255,255,255,0.85)', padding: '5px 10px', borderRadius: 999 }}>✓ Cancele quando quiser</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button onClick={() => navigate({ to: '/login' })} style={{ background: '#05070a', color: '#fff', fontWeight: 800, fontSize: 14, padding: '13px 18px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.18)', cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}>Criar minha conta grátis →</button>
                <button onClick={() => scrollTo('pricing')} style={{ background: 'rgba(255,255,255,0.92)', color: '#05070a', fontWeight: 800, fontSize: 14, padding: '13px 18px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.9)', cursor: 'pointer' }}>Ver preços</button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ── FOOTER NEWSLETTER PREMIUM ── */}
      <footer style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.06)', background: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.18))', backdropFilter: 'blur(10px)' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '22px 20px 18px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 0.8fr 0.8fr', gap: 18, alignItems: 'start' }} className="footer-grid">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, #06b6d4, #f59e0b)', display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 800 }}>◆</span>
                <span style={{ fontFamily: "'Sora', Inter, sans-serif", fontWeight: 800, letterSpacing: '-0.03em', color: '#fff' }}>Nexora</span>
                <span style={{ fontSize: 10, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.45)', fontWeight: 700 }}>ERP</span>
              </div>
              <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6, margin: '10px 0 12px', maxWidth: 380 }}>O ERP que descomplica o Brasil real. Vendas, estoque, financeiro e produção — rápido, seguro e sem planilhas.</p>
              <form onSubmit={handleNewsletter} style={{ display: 'flex', gap: 8, maxWidth: 380 }} aria-label="Assinar newsletter">
                <label htmlFor="nl-email" style={{ position: 'absolute', left: -9999 }}>E-mail para novidades</label>
                <input
                  id="nl-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  aria-label="E-mail para newsletter"
                  style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', borderRadius: 999, padding: '11px 14px', fontSize: 13, outline: 'none' }}
                />
                <button type="submit" aria-label="Assinar newsletter" style={{ background: 'linear-gradient(135deg, #06b6d4, #0ea5e9)', color: '#05070a', fontWeight: 800, fontSize: 13, padding: '11px 16px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.18)', cursor: 'pointer', whiteSpace: 'nowrap' }}>{emailOk ? '✓ Inscrito!' : 'Assinar'}</button>
              </form>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>1 e-mail/semana · sem spam · cancele quando quiser</div>
            </div>
            {[
              { title: 'Produto', links: ['Funcionalidades', 'Preços', 'Demonstração', 'Atualizações', 'Roadmap'] },
              { title: 'Empresa', links: ['Sobre nós', 'Carreiras', 'Blog', 'Parceiros', 'Contato'] },
              { title: 'Suporte', links: ['Central de ajuda', 'Documentação', 'Status', 'LGPD & Privacidade', 'Termos'] },
            ].map((col) => (
              <div key={col.title}>
                <div style={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#e2e8f0', marginBottom: 10 }}>{col.title}</div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 7 }}>
                  {col.links.map((l) => (
                    <li key={l}><a href="#" onClick={(e) => e.preventDefault()} style={{ color: '#94a3b8', textDecoration: 'none', fontSize: 13, fontWeight: 500 }}>{l}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginTop: 18, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: 12, color: '#64748b' }}>
            <span>© {new Date().getFullYear()} Nexora · CNPJ 00.000.000/0001-00 · Todos os direitos reservados.</span>
            <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: '#10b981', boxShadow: '0 0 10px rgba(16,185,129,0.5)' }} /> Sistemas operacionais · 99,9% uptime
            </span>
          </div>
        </div>
      </footer>

      {/* ── Global Styles ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Inter:wght@400;500;600;700;800&display=swap');
        * { scroll-behavior: smooth; }
        ::selection { background: rgba(6,182,212,0.28); color: #fff; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #05070a; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.10); border-radius: 999px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.18); }
        :focus-visible { outline: 2px solid #06b6d4; outline-offset: 2px; }
        .nav-link-underline::after { content:''; position:absolute; left:14px; right:14px; bottom:2px; height:2px; border-radius:999px; background: linear-gradient(90deg, #06b6d4, #f59e0b); transform: scaleX(0); transform-origin: left; transition: transform 180ms cubic-bezier(0.16,1,0.3,1); opacity:0.95; }
        .nav-link-underline:hover::after, .nav-link-underline:focus-visible::after { transform: scaleX(1); }
        .btn-shimmer-premium::after { content:''; position:absolute; inset:0; background: linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.22) 50%, transparent 70%); transform: translateX(-100%); transition: transform 640ms cubic-bezier(0.16,1,0.3,1); }
        .btn-shimmer-premium:hover::after { transform: translateX(100%); }
        .btn-shimmer-premium:hover { transform: translateY(-1px); box-shadow: 0 0 28px rgba(6,182,212,0.42), 0 6px 20px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.40); }
        .btn-shimmer-premium:active { transform: translateY(0); }
        .bento-card:hover { transform: translateY(-4px); border-color: rgba(255,255,255,0.14) !important; box-shadow: 0 16px 48px rgba(0,0,0,0.55), 0 4px 16px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08) !important; }
        .bento-card:hover .bento-glow { opacity: 1 !important; }
        @keyframes floatA { 0%,100%{ transform: translateY(0) } 50%{ transform: translateY(-8px) } }
        @keyframes floatB { 0%,100%{ transform: translateY(0) } 50%{ transform: translateY(-10px) } }
        @keyframes floatC { 0%,100%{ transform: translateY(0) } 50%{ transform: translateY(-6px) } }
        .float-a { animation: floatA 5.2s ease-in-out infinite; }
        .float-b { animation: floatB 5.8s ease-in-out infinite 0.6s; }
        .float-c { animation: floatC 4.6s ease-in-out infinite 0.3s; }
        .float-main { animation: floatA 7s ease-in-out infinite; }
        @media (max-width: 980px) {
          .hero-grid { grid-templateColumns: 1fr !important; gap: 18px !important; padding-top: 78px !important; }
          .hero-grid > div:last-child { order: -1; }
          .stats-grid { grid-templateColumns: repeat(2,1fr) !important; }
          .bento-grid { grid-templateColumns: 1fr !important; }
          .bento-grid > div { grid-column: span 1 !important; }
          .showcase-grid { grid-templateColumns: 1fr !important; }
          .pricing-grid { grid-templateColumns: 1fr !important; }
          .pricing-grid > div:nth-child(2) { transform: none !important; }
          .cta-grid { grid-templateColumns: 1fr !important; }
          .footer-grid { grid-templateColumns: 1fr 1fr !important; }
          .nav-links { display:none !important; }
        }
        @media (max-width: 560px) {
          .footer-grid { grid-templateColumns: 1fr !important; }
          .stats-grid { grid-templateColumns: 1fr 1fr !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
          .float-a, .float-b, .float-c, .float-main { animation: none !important; }
          .bento-card:hover { transform: none !important; }
        }
      `}</style>
    </div>
  );
}

// ── Local styles ───────────────────────────────────────────
const badgeStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '5px 10px', borderRadius: 999,
  background: 'rgba(6,182,212,0.10)', border: '1px solid rgba(6,182,212,0.18)',
  color: '#06b6d4', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
};
const h2Style: React.CSSProperties = {
  fontFamily: "'Sora', Inter, sans-serif",
  fontWeight: 800, fontSize: 'clamp(22px, 3vw, 32px)', letterSpacing: '-0.03em', color: '#fff', margin: '8px 0 8px', lineHeight: 1.15, textAlign: 'center',
};
const subStyle: React.CSSProperties = {
  color: '#94a3b8', fontSize: 14, lineHeight: 1.6, maxWidth: 640, margin: '0 auto', textAlign: 'center',
};
const carouselArrow: React.CSSProperties = {
  width: 34, height: 34, borderRadius: 999, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.06)', color: '#e2e8f0', cursor: 'pointer', display: 'grid', placeItems: 'center', fontSize: 16, fontWeight: 800,
};
const kpiMini: React.CSSProperties = {
  borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', padding: '8px 10px', fontSize: 11, color: '#94a3b8', fontWeight: 600,
};
